/**
 * 担当案件テーブルに「アンケート行を必ず 1 行出す」ための純粋関数群。
 *
 * デザイン view の案件一覧は **未完了デザイン子がある母票**しか出さない。一方アンケートは
 * **完了した子票**にしか出ないので、デザインが全部終わった案件は一覧から落ちたまま
 * 未回答だけが残る、という取りこぼしが構造的に起きる。ここで「行が描かれない未回答」を
 * 拾い上げ、テーブル末尾に母票単位で足す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 案件一覧から落ちた母票のアンケート（未回答 + 回答直後 1 週間）を拾って行に起こす
 * @graph-connects isStaleAnsweredRow [calls] 回答済み行の保持期間判定
 */
import type { EvaluationTask } from '../../../types/idt-design-evaluation.js';
import { isStaleAnsweredRow } from './settled-row-retention.js';

/** 案件一覧に行が無い母票 1 件ぶんのアンケート。 @graph-connects none */
export interface SurveyOnlyGroup {
  parentIssueKey: string;
  parentSummary: string;
  tasks: EvaluationTask[];
}

/**
 * 指定した index を先頭に持ってくる。モーダルは先頭から順に回答していくので、
 * 「クリックした件から始めて、残りも続けて消化できる」状態にする。
 * @graph-connects none
 */
export function orderedFrom(tasks: EvaluationTask[], index: number): EvaluationTask[] {
  if (index <= 0) return tasks;
  return [...tasks.slice(index), ...tasks.slice(0, index)];
}

/**
 * 既に行が描かれている子票キーの集合を渡すと、**そこに含まれないアンケート**を母票ごとに
 * 束ねて返す。
 *
 * 判定を母票キーではなく**子票キー**で行うのが要点。母票が一覧にあっても、その子票が
 * `designChildren` に出てこない（データソース間のズレ）ケースまで拾える。
 *
 * **回答済みも回答日から 1 週間は拾う。** デザインが全部終わった母票は
 * `hasActiveDesign` で一覧から落ちるので、その子票はこの orphan 経路でしか行に出ない。
 * 回答した瞬間に `pending` でなくなって行ごと消えると、自分の回答が通ったことを
 * 一覧で確認できない（`visibleDesignChildren` 側だけ 1 週間残す実装になっていて、
 * orphan 経路が取り残されていた）。
 * @graph-connects isStaleAnsweredRow [calls] 回答済み行の保持期間判定
 */
export function orphanSurveyGroups(
  tasks: EvaluationTask[],
  renderedChildKeys: ReadonlySet<string>,
  today: string,
): SurveyOnlyGroup[] {
  const groups = new Map<string, SurveyOnlyGroup>();
  for (const task of tasks) {
    if (isStaleAnsweredRow(task, today)) continue;
    if (renderedChildKeys.has(task.childIssueKey)) continue;
    const group = groups.get(task.parentIssueKey);
    if (group) {
      group.tasks.push(task);
    } else {
      groups.set(task.parentIssueKey, {
        parentIssueKey: task.parentIssueKey,
        parentSummary: task.parentSummary,
        tasks: [task],
      });
    }
  }
  return [...groups.values()];
}
